import React from 'react';
import { TrendingUp, DollarSign, Target, CheckCircle2, Calendar, Users } from 'lucide-react';

const BoardMetrics = ({ boardType, tasks }) => {
  if (!tasks || tasks.length === 0) return null;

  const renderProjectMetrics = () => {
    const completedTasks = tasks.filter(t => t.status.value === 'done' || t.status.label === 'Done').length;
    const totalTasks = tasks.length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    const avgProgress = tasks.reduce((sum, t) => sum + (t.progress || 0), 0) / totalTasks;

    return (
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-900">Total Tasks</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{totalTasks}</p>
          <p className="text-xs text-blue-600 mt-1">{completedTasks} completed</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-green-900">Completion Rate</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{completionRate}%</p>
          <div className="w-full bg-green-200 rounded-full h-2 mt-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all"
              style={{ width: `${completionRate}%` }}
            ></div>
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-purple-600" />
            <span className="text-xs font-medium text-purple-900">Avg Progress</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">{Math.round(avgProgress)}%</p>
          <p className="text-xs text-purple-600 mt-1">across all tasks</p>
        </div>
      </div>
    );
  };

  const renderMarketingMetrics = () => {
    const activeCampaigns = tasks.filter(t => 
      t.status.value === 'in_production' || 
      t.status.label === 'In Production' ||
      t.status.value === 'published' || 
      t.status.label === 'Published'
    ).length;
    const totalCampaigns = tasks.length;
    const channelBreakdown = tasks.reduce((acc, task) => {
      const channel = task.channel || 'Other';
      acc[channel] = (acc[channel] || 0) + 1;
      return acc;
    }, {});
    const topChannel = Object.entries(channelBreakdown).sort((a, b) => b[1] - a[1])[0];

    return (
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-orange-600" />
            <span className="text-xs font-medium text-orange-900">Total Campaigns</span>
          </div>
          <p className="text-2xl font-bold text-orange-600">{totalCampaigns}</p>
          <p className="text-xs text-orange-600 mt-1">{activeCampaigns} active</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-green-900">Published</span>
          </div>
          <p className="text-2xl font-bold text-green-600">
            {tasks.filter(t => t.status.value === 'published' || t.status.label === 'Published').length}
          </p>
          <p className="text-xs text-green-600 mt-1">campaigns live</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-900">Top Channel</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{topChannel?.[0] || 'N/A'}</p>
          <p className="text-xs text-blue-600 mt-1">{topChannel?.[1] || 0} campaigns</p>
        </div>
      </div>
    );
  };

  const renderSalesMetrics = () => {
    const totalValue = tasks.reduce((sum, t) => sum + (t.deal_value || 0), 0);
    const wonDeals = tasks.filter(t => t.status.value === 'won' || t.status.label === 'Won');
    const wonValue = wonDeals.reduce((sum, t) => sum + (t.deal_value || 0), 0);
    const avgDealValue = totalValue / tasks.length || 0;
    const conversionRate = tasks.length > 0 ? Math.round((wonDeals.length / tasks.length) * 100) : 0;

    return (
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-green-900">Total Pipeline</span>
          </div>
          <p className="text-2xl font-bold text-green-600">
            €{totalValue.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
          </p>
          <p className="text-xs text-green-600 mt-1">{tasks.length} deals</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-900">Won Deals</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            €{wonValue.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
          </p>
          <p className="text-xs text-blue-600 mt-1">{wonDeals.length} deals closed</p>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-purple-600" />
            <span className="text-xs font-medium text-purple-900">Conversion Rate</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">{conversionRate}%</p>
          <p className="text-xs text-purple-600 mt-1">avg €{avgDealValue.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-6">
      {boardType === 'project' && renderProjectMetrics()}
      {boardType === 'marketing' && renderMarketingMetrics()}
      {boardType === 'sales' && renderSalesMetrics()}
    </div>
  );
};

export default BoardMetrics;
